import React, { useEffect, useState } from "react";
import { loadStyles } from "../../utils/cssLoader";
import { getData } from "../../utils/getData";

/**
 * F1RaceDayHero — "Race-day hero": a single Grand Prix detail card.
 *
 *   • Banner: GRAND PRIX tag + venue, GP name, weekend date range.
 *   • Lights Out: the Race session's day + start time (the hero moment).
 *   • Support Programme: every other session grouped by day (FRI/SAT/SUN), cancelled ones marked.
 *
 * Data: formula1/races (list) → pick the race by data-id, else the current race (by today's date).
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_ABBR = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
};
const fmtRange = (a, b) => {
  const d1 = parseDate(a);
  if (!d1) return "";
  const d2 = parseDate(b) || d1;
  const m1 = MONTHS[d1.getMonth()];
  if (d1.getMonth() === d2.getMonth()) {
    return d2.getDate() !== d1.getDate()
      ? `${d1.getDate()} - ${d2.getDate()} ${m1}`
      : `${d1.getDate()} ${m1}`;
  }
  return `${d1.getDate()} ${m1} - ${d2.getDate()} ${MONTHS[d2.getMonth()]}`;
};
const fmtHM = (s) => {
  const d = parseDate(s);
  if (!d) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const fmtDayFull = (s) => {
  const d = parseDate(s);
  if (!d) return "";
  return `${DAY_FULL[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

const racesFromRaw = (raw) => {
  const d = raw?.data ?? raw;
  return Array.isArray(d) ? d : [];
};

// Finishing order rows → {pos, name, team, time, gap}. P1's "gap" is the full time; others are +delta.
const mapResults = (raw) =>
  racesFromRaw(raw)
    .map((e) => ({
      pos: e?.pos,
      name: e?.nm || e?.ply || "",
      team: e?.team || "",
      time: e?.time || "",
      gap: e?.gap || "",
    }))
    .filter((r) => r.name)
    .sort((a, b) => (a.pos ?? 999) - (b.pos ?? 999));

// Pick the race by data-id, else the current one (first not finished by `date`, else last).
const pickRace = (races, dataId, dateStr) => {
  if (!races.length) return null;
  if (dataId != null) {
    const found = races.find((r) => String(r?.id) === String(dataId));
    if (found) return found;
  }
  const sorted = [...races].sort(
    (a, b) => (parseDate(a.race_start)?.getTime() || 0) - (parseDate(b.race_start)?.getTime() || 0)
  );
  const today = parseDate(dateStr) || new Date();
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const i = sorted.findIndex((r) => {
    const e = parseDate(r.race_end) || parseDate(r.race_start);
    return e && e.getTime() >= t0;
  });
  return i < 0 ? sorted[sorted.length - 1] : sorted[i];
};

// Support programme grouped by day (chronological), excluding the main Race + deleted sessions.
const buildProgramme = (sessions, raceSession) => {
  const items = (sessions || [])
    .filter((s) => s?.status !== "Deleted" && s !== raceSession)
    .sort((a, b) => (parseDate(a.dt)?.getTime() || 0) - (parseDate(b.dt)?.getTime() || 0));
  const groups = [];
  items.forEach((s) => {
    const d = parseDate(s.dt);
    const key = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : "?";
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: d ? DAY_ABBR[d.getDay()] : "", items: [] };
      groups.push(g);
    }
    g.items.push(s);
  });
  return groups;
};

const F1RaceDayHero = ({
  serverData = null,
  data: dataProp = null,
  dataId,
  baseUrl,
  sport = "formula1",
  loadCSS = true,
  dataClient = false,
  date = null,
}) => {
  useEffect(() => {
    loadStyles("f1RaceDayHero");
  }, []);

  const [races, setRaces] = useState(() => racesFromRaw(serverData || dataProp));
  const [results, setResults] = useState([]);
  const [nowTs, setNowTs] = useState(null); // ticking clock for the upcoming countdown

  useEffect(() => {
    setNowTs(Date.now());
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const r = racesFromRaw(serverData || dataProp);
    if (r.length) setRaces(r);
  }, [serverData, dataProp]);

  useEffect(() => {
    if (!dataClient) return;
    try {
      const el = document.querySelector(".microservice-cont");
      const parsed = JSON.parse(el?.getAttribute("data-listingdata") || "null");
      const r = racesFromRaw(parsed?.data);
      if (r.length) setRaces(r);
    } catch (_) {}
  }, [dataClient]);

  useEffect(() => {
    if (races.length || !baseUrl) return;
    let cancelled = false;
    getData({ dataType: "f1-race-hero", baseUrl, sport }).then((res) => {
      if (cancelled) return;
      const r = racesFromRaw(res);
      if (r.length) setRaces(r);
    });
    return () => {
      cancelled = true;
    };
  }, [races.length, baseUrl, sport]);

  const race = pickRace(races, dataId, date);

  const todayStart = (() => {
    const t = parseDate(date) || new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  })();
  const dayStart = (s) => {
    const d = parseDate(s);
    return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() : null;
  };

  // Three states: finished (end < today) · upcoming (start > today) · current (in between).
  const endDay = dayStart(race?.race_end) ?? dayStart(race?.race_start);
  const startDay = dayStart(race?.race_start);
  const raceEnded = endDay != null && endDay < todayStart;
  const isUpcoming = !raceEnded && startDay != null && startDay > todayStart;

  // Race classification (finishing order) — lazy fetch, only for old/finished races.
  useEffect(() => {
    if (!race?.id || !baseUrl || !raceEnded) {
      setResults([]);
      return;
    }
    let cancelled = false;
    getData({ dataId: race.id, dataType: "f1-race-results", baseUrl, sport }).then((res) => {
      if (cancelled) return;
      setResults(mapResults(res));
    });
    return () => {
      cancelled = true;
    };
  }, [race?.id, baseUrl, sport, raceEnded]);

  if (!race) {
    return (
      <div className="f1h f1h--loading">
        <span>Loading race…</span>
      </div>
    );
  }

  const sessions = (race.race_types || []).filter((s) => s?.status !== "Deleted");
  const raceSession = sessions.find((s) => /^race$/i.test(s?.nm || ""));
  const days = buildProgramme(sessions, raceSession);
  const venue = race.venue?.name || "";

  // Countdown to lights out (Race session, else weekend start) for upcoming races.
  const cdTarget = parseDate(raceSession?.dt) || parseDate(race.race_start);
  const cdMs = cdTarget && nowTs != null ? Math.max(0, cdTarget.getTime() - nowTs) : 0;
  const cd = [
    { n: Math.floor(cdMs / 86400000), u: "Days" },
    { n: Math.floor((cdMs % 86400000) / 3600000), u: "Hrs" },
    { n: Math.floor((cdMs % 3600000) / 60000), u: "Min" },
    { n: Math.floor((cdMs % 60000) / 1000), u: "Sec" },
  ];

  return (
    <div className="f1h">
      {/* Hero: banner + Lights Out in one section */}
      <div className="f1h__hero">
        <div className="f1h__banner-top">
          <span className="f1h__tag">
            <svg className="f1h__ic" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M6 3a1 1 0 0 1 2 0v18a1 1 0 1 1-2 0V3z" />
              <path d="M8 4l11 4-11 4V4z" />
            </svg>
            Grand Prix
          </span>
          {venue ? (
            <span className="f1h__venue">
              <svg className="f1h__ic" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M8 1a5 5 0 0 0-5 5c0 3.5 5 9 5 9s5-5.5 5-9a5 5 0 0 0-5-5zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
              </svg>
              {venue}
            </span>
          ) : null}
        </div>
        <h3 className="f1h__title">{race.nm}</h3>
        <span className="f1h__range">{fmtRange(race.race_start, race.race_end)}</span>

        {raceSession ? (
          <div className="f1h__lights">
            <div className="f1h__lights-row">
              <div className="f1h__lights-left">
                <span className="f1h__lights-lbl">Lights Out</span>
                <span className="f1h__lights-day">{fmtDayFull(raceSession.dt)}</span>
              </div>
              <span className="f1h__lights-time">{fmtHM(raceSession.dt)}</span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Upcoming — countdown to lights out */}
      {isUpcoming ? (
        <div className="f1h__cd-sec">
          <span className="f1h__prog-lbl">Countdown to Lights Out</span>
          <div className="f1h__cd">
            {cd.map((c, i) => (
              <div className="f1h__cd-box" key={i}>
                <span className="f1h__cd-num">{String(c.n).padStart(2, "0")}</span>
                <span className="f1h__cd-unit">{c.u}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Support programme — shown while the race is current / upcoming */}
      {!raceEnded && days.length ? (
        <div className="f1h__prog">
          <span className="f1h__prog-lbl">Support Programme</span>
          {days.map((g, i) => (
            <div className="f1h__day" key={i}>
              <span className="f1h__day-lbl">{g.label}</span>
              <div className="f1h__day-list">
                {g.items.map((s, j) => {
                  const cancelled = s.status === "Cancelled";
                  return (
                    <div
                      key={j}
                      className={`f1h__sess${cancelled ? " is-cancelled" : ""}`}
                    >
                      <span className="f1h__sess-nm">
                        {s.nm}
                        {cancelled ? (
                          <svg className="f1h__sess-x" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                            <circle cx="8" cy="8" r="6" />
                            <path d="M5.5 5.5l5 5" strokeLinecap="round" />
                          </svg>
                        ) : null}
                      </span>
                      <span className="f1h__sess-time">{fmtHM(s.dt)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Race classification (finishing order) — shown once the race is finished */}
      {raceEnded && results.length ? (
        <div className="f1h__results">
          <span className="f1h__prog-lbl">Race Result</span>
          <div className="f1h__res-list">
            {results.map((r, i) => {
              const gap = r.pos === 1 ? r.time || r.gap : r.gap ? `+${r.gap}` : "";
              const podium = r.pos >= 1 && r.pos <= 3 ? ` p${r.pos}` : "";
              return (
                <div className="f1h__res-row" key={i}>
                  <span className={`f1h__res-pos${podium}`}>{r.pos}</span>
                  <span className="f1h__res-nm">
                    <span className="f1h__res-name">{r.name}</span>
                    {r.team ? <span className="f1h__res-team">{r.team}</span> : null}
                  </span>
                  {gap ? <span className="f1h__res-gap">{gap}</span> : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default F1RaceDayHero;
